/* PLATONIC — homepage interactions: tile galleries, For Him/Her filter,
   and add-to-cart (real Shopify cart add). Ported from the prototype. */

document.addEventListener('DOMContentLoaded', () => {
  initTileGalleries();
  initTileFilter();
  initTileATC();
  initTilesCarousel();
  initFeatureVideo();
  initDeferredBackgrounds();
  initAfterHero();
});

/* Swap a deferred image/video poster in (data-src / data-srcset / data-poster). */
function hydrate(el) {
  const d = el.dataset;
  if (d.srcset) { el.srcset = d.srcset; el.removeAttribute('data-srcset'); }
  if (d.src) { el.src = d.src; el.removeAttribute('data-src'); }
  if (d.poster) { el.poster = d.poster; el.removeAttribute('data-poster'); }
}

/* Below-the-hero imagery (Essential Six tiles 2-6, pair band, In Focus poster) waits
   until the hero picture has loaded, so on a slow phone the hero gets the whole
   connection. Chrome's loading="lazy" can't do this — on slow networks it fetches
   anything within ~2500px, i.e. most of the homepage (speed audit 2026-11-04). */
function initAfterHero() {
  const els = document.querySelectorAll('[data-after-hero]');
  if (!els.length) return;
  let done = false;
  const go = () => { if (done) return; done = true; els.forEach(hydrate); };
  const hero = document.querySelector('img[fetchpriority="high"]');
  if (!hero || hero.complete) { go(); return; }
  hero.addEventListener('load', go, { once: true });
  hero.addEventListener('error', go, { once: true });
  window.addEventListener('load', go, { once: true });
  document.addEventListener('touchstart', go, { once: true, passive: true });
}

/* Founder band photo (CSS background) only once the band is ~1 screen away —
   see .tf-housestory:not(.bg-ready) in platonic-home.css (speed audit 2026-11-04). */
function initDeferredBackgrounds() {
  const bands = document.querySelectorAll('.tf-housestory');
  if (!bands.length) return;
  if (!('IntersectionObserver' in window)) { bands.forEach((b) => b.classList.add('bg-ready')); return; }
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('bg-ready'); obs.unobserve(e.target); } });
  }, { rootMargin: '100% 0px 100% 0px' });
  bands.forEach((b) => io.observe(b));
}

/* Feature "In Focus" video — it sits below the fold, so it must NOT autoplay or
   download on page load (the markup drops `autoplay` and sets preload="none").
   Play only while it's in view; pause when it scrolls away, to save bandwidth,
   CPU and battery. Reduced-motion users never get autoplay — the poster stands
   in. Falls back to plain play() where IntersectionObserver is unavailable. */
function initFeatureVideo() {
  const videos = document.querySelectorAll('.tf-feature-image video');
  if (!videos.length) return;

  // Honour the OS reduced-motion preference — leave the poster, never autoplay.
  if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const play = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };

  if (!('IntersectionObserver' in window)) {
    videos.forEach(play); // no IO → just run the film (still muted + playsinline)
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { e.isIntersecting ? play(e.target) : e.target.pause(); });
  }, { threshold: 0.25 });
  videos.forEach((v) => io.observe(v));
}

/* Tom Ford-style carousel nav — the "1 / 6" counter + arrows that track the
   homepage Essential Six swipe. Phones only (the section has .tf-tiles--carousel;
   the nav is display:none elsewhere). No-ops on the grid/collection pages. */
function initTilesCarousel() {
  const section = document.querySelector('.tf-tiles--carousel');
  if (!section) return;
  const grid = section.querySelector('.tf-tiles-grid');
  const nav = section.querySelector('.tf-carousel-nav');
  if (!grid || !nav) return;
  const prev = nav.querySelector('[data-carousel-prev]');
  const next = nav.querySelector('[data-carousel-next]');
  const curEl = nav.querySelector('[data-carousel-current]');
  const totalEl = nav.querySelector('[data-carousel-total]');
  const cards = () => Array.prototype.slice.call(grid.querySelectorAll('.tf-tile')).filter((c) => c.offsetParent !== null);
  const step = () => { const c = cards()[0]; return c ? c.getBoundingClientRect().width + 16 : grid.clientWidth; };
  const update = () => {
    const cs = cards();
    if (totalEl) totalEl.textContent = cs.length;
    if (!cs.length) return;
    const idx = Math.round(grid.scrollLeft / step());
    // Clamp to 1..total — never 0 (iOS rubber-band overscroll can push scrollLeft negative).
    if (curEl) curEl.textContent = Math.max(1, Math.min(cs.length, idx + 1));
  };
  grid.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
  // Arrows cycle: next past the last wraps to the first; prev before the first wraps to the last.
  // Respect the OS reduced-motion preference (checked per-click so it stays live).
  const scrollBehavior = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
  if (prev) prev.addEventListener('click', () => {
    const max = grid.scrollWidth - grid.clientWidth;
    grid.scrollTo({ left: grid.scrollLeft <= 2 ? max : grid.scrollLeft - step(), behavior: scrollBehavior() });
  });
  if (next) next.addEventListener('click', () => {
    const max = grid.scrollWidth - grid.clientWidth;
    grid.scrollTo({ left: grid.scrollLeft >= max - 2 ? 0 : grid.scrollLeft + step(), behavior: scrollBehavior() });
  });
  // On filter change (For Him / For Her / All Six), snap back to the first
  // product and recompute the total so the counter reads 1/N, not the old index.
  section.querySelectorAll('.tf-filter-btn').forEach((b) => b.addEventListener('click', () => setTimeout(() => {
    grid.scrollLeft = 0;
    update();
  }, 60)));
  window.addEventListener('resize', () => window.requestAnimationFrame(update));
  update();
}

/* For Him / For Her filter — re-sorts the same six. Unisex (data-gender="u")
   surface under both. Membership + order defined here. */
function initTileFilter() {
  const bar = document.querySelector('.tf-tiles-filter');
  const grid = document.querySelector('.tf-tiles-grid');
  if (!bar || !grid) return;
  const tiles = Array.from(grid.querySelectorAll('.tf-tile--gallery'));
  const btns = Array.from(bar.querySelectorAll('.tf-filter-btn'));
  const ORDER = {
    m: { diplomat: 1, xy: 2, saltwater: 3, monolith: 4 },
    f: { nightbloom: 1, quest: 2, xy: 3, saltwater: 4 },
  };
  // Read the scent slug from the tile itself (always present, even when the
  // tile is sold out and has no add-to-cart button).
  const slug = (t) => t.dataset.product || null;
  const apply = (f) => {
    btns.forEach((b) => {
      const on = b.dataset.filter === f;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    tiles.forEach((t) => {
      if (f === 'all') {
        t.classList.remove('is-hidden');
        t.style.order = '';
        return;
      }
      const ord = ORDER[f][slug(t)];
      const show = ord !== undefined;
      t.classList.toggle('is-hidden', !show);
      t.style.order = show ? String(ord) : '';
    });
  };
  bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.tf-filter-btn');
    if (btn) apply(btn.dataset.filter);
  });
}

/* Tile galleries — Ferragamo-style slide track + arrow navigation. */
function initTileGalleries() {
  const tiles = document.querySelectorAll('[data-tile-gallery]');
  if (!tiles.length) return;

  // Eagerly load every slide of a tile once it nears the viewport, so browsing the
  // gallery never shows a blank/half-loaded slide (lazy + off-screen-in-track would).
  // Speed audit 2026-11-04: running this at once made ~24 hidden slides (~1-2MB) download
  // at ~2.3s on phones, alongside the hero / first tile — LCP 10-11s on slow 4G. Now the
  // warm-up waits for the page load event (+ idle); touching or hovering a tile warms
  // that tile immediately, so browsing still never shows a blank slide.
  // Slides 2-4 + the hover twin ship as data-src/data-srcset (a 1px placeholder in src):
  // Chrome's own loading="lazy" fetches anything within ~2500px on slow phones — the whole
  // carousel — so the attribute alone never held them back. Hydrate here instead.
  const eagerTile = (t) => t.querySelectorAll('.tf-tile-slide, .tf-tile-hover').forEach((img) => { hydrate(img); img.loading = 'eager'; });
  tiles.forEach((t) => {
    t.addEventListener('touchstart', () => eagerTile(t), { once: true, passive: true });
    t.addEventListener('pointerenter', () => eagerTile(t), { once: true, passive: true });
    t.addEventListener('focusin', () => eagerTile(t), { once: true });
  });
  const startWarm = () => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        eagerTile(e.target);
        obs.unobserve(e.target);
      });
    }, { rootMargin: '300px' });
    tiles.forEach((t) => io.observe(t));
  };
  const idleWarm = () => { if ('requestIdleCallback' in window) requestIdleCallback(startWarm, { timeout: 2000 }); else setTimeout(startWarm, 300); };
  if (document.readyState === 'complete') idleWarm(); else window.addEventListener('load', idleWarm, { once: true });

  tiles.forEach((tile) => {
    const track = tile.querySelector('[data-tile-track]');
    const slides = track ? track.querySelectorAll('.tf-tile-slide') : [];
    if (!track || slides.length < 2) return;

    const total = slides.length;
    const fill = tile.querySelector('[data-tile-fill]');
    if (fill) fill.style.width = (100 / total) + '%';
    let index = 0;

    const setIndex = (i) => {
      index = ((i % total) + total) % total;
      track.style.transform = `translateX(-${index * 100}%)`;
      if (fill) fill.style.transform = `translateX(${index * 100}%)`;
      tile.classList.toggle('is-explored', index !== 0);
    };

    let hovering = false;
    const stage = tile.querySelector('.tf-tile-stage') || tile;
    // Only track hover on real-pointer devices — on touch a tap fires a sticky
    // mouseenter, which would leave `hovering` true and make the next arrow skip
    // a slide. Mobile gets plain slide-by-slide arrow nav instead.
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      stage.addEventListener('mouseenter', () => { hovering = true; });
      stage.addEventListener('mouseleave', () => {
        hovering = false;
        // Founder call 2026-08-15: a tile browsed with the arrows snaps back to
        // the default first slide once the cursor leaves the frame.
        if (index !== 0) setIndex(0);
      });
    }

    // Mobile counterpart of the mouseleave snap-back (founder call 2026-08-15):
    // once a browsed tile scrolls fully out of view, reset it to the first slide.
    // The jump is done with transitions suppressed — it happens off screen, so
    // the tile simply reappears in its default state. Also covers desktop
    // scroll-past where the cursor never left the frame.
    if ('IntersectionObserver' in window) {
      const resetIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting || index === 0) return;
          track.style.transition = 'none';
          if (fill) fill.style.transition = 'none';
          setIndex(0);
          void track.offsetWidth;
          requestAnimationFrame(() => {
            track.style.transition = '';
            if (fill) fill.style.transition = '';
          });
        });
      }, { threshold: 0 });
      resetIO.observe(tile);
    }

    const prev = tile.querySelector('[data-tile-prev]');
    const next = tile.querySelector('[data-tile-next]');

    if (prev) prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIndex(index - 1);
    });

    if (next) next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (index === 0 && hovering) {
        track.style.transition = 'none';
        if (fill) fill.style.transition = 'none';
        track.style.transform = 'translateX(-100%)';
        if (fill) fill.style.transform = 'translateX(100%)';
        index = 1;
        tile.classList.add('is-explored');
        void track.offsetWidth;
        requestAnimationFrame(() => {
          track.style.transition = '';
          if (fill) fill.style.transition = '';
          setIndex(2);
        });
      } else {
        setIndex(index + 1);
      }
    });

    // Touch-swipe the gallery on the collection (fragrance) page: the drag follows the
    // finger and snaps to the next/prev image; a plain tap still opens the PDP. Scoped to
    // .tf-tiles--page so it never fights the homepage carousel's own horizontal scroll.
    if (tile.closest('.tf-tiles--page')) {
      const link = tile.querySelector('.tf-tile-link');
      let sx = 0, sy = 0, dragging = false, horizontal = null, didSwipe = false, w = 1;
      stage.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) { dragging = false; return; }
        sx = e.touches[0].clientX; sy = e.touches[0].clientY;
        w = stage.getBoundingClientRect().width || 1;
        dragging = true; horizontal = null; didSwipe = false;
      }, { passive: true });
      stage.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        const dx = e.touches[0].clientX - sx, dy = e.touches[0].clientY - sy;
        if (horizontal === null) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          horizontal = Math.abs(dx) > Math.abs(dy);
        }
        if (!horizontal) return;          // vertical intent → let the page scroll
        e.preventDefault();               // claim the horizontal gesture
        let pct = (dx / w) * 100;
        if ((index === 0 && pct > 0) || (index === total - 1 && pct < 0)) pct *= 0.3; // edge resistance
        track.style.transition = 'none';
        if (fill) fill.style.transition = 'none';
        track.style.transform = `translateX(${-index * 100 + pct}%)`;
      }, { passive: false });
      const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        track.style.transition = '';
        if (fill) fill.style.transition = '';
        const dx = (e.changedTouches ? e.changedTouches[0].clientX : sx) - sx;
        if (horizontal && Math.abs(dx) > w * 0.16) {
          if (dx < 0 && index < total - 1) { didSwipe = true; setIndex(index + 1); }
          else if (dx > 0 && index > 0) { didSwipe = true; setIndex(index - 1); }
          else setIndex(index);           // at an edge → snap back
        } else {
          setIndex(index);                // below threshold → snap back
        }
      };
      stage.addEventListener('touchend', endDrag);
      stage.addEventListener('touchcancel', endDrag);
      if (link) link.addEventListener('click', (e) => { if (didSwipe) { e.preventDefault(); didSwipe = false; } });
    }
  });
}

/* Add-to-Cart — real Shopify cart add via /cart/add.js. On success, refresh the
   header cart count and route to the cart page (the slide-out drawer is a later
   milestone). On failure (e.g. out of stock), fall back to the product page. */
function initTileATC() {
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  document.querySelectorAll('[data-atc]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const id = btn.dataset.variantId;
      const fallback = btn.dataset.href;
      if (!id) { if (fallback) window.location.href = fallback; return; }
      const original = btn.querySelector('span') ? btn.querySelector('span').textContent : null;
      btn.setAttribute('aria-busy', 'true');
      try {
        if (window.PlatonicCart && window.PlatonicCart.addVariant) {
          // Route through the drawer's addVariant, which sets the line to an ABSOLUTE
          // quantity capped at MAX_QTY — so repeated taps can't push a line past the cap
          // (a raw additive /cart/add.js could). refresh() first so the cap is computed
          // against the true current cart and never clobbers an existing line.
          await window.PlatonicCart.refresh();
          await window.PlatonicCart.addVariant(Number(id), 1);
          btn.removeAttribute('aria-busy');
        } else {
          const res = await fetch(addUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('add failed');
          window.location.href = cartUrl;
        }
      } catch (err) {
        btn.removeAttribute('aria-busy');
        if (fallback) window.location.href = fallback;
      }
    });
  });
}
